import { useContext } from 'react';

import {
  InfiniteData,
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { HeaderContentQueryKey } from '@/common/hooks/useHeaderContent/useHeaderContentInfo';
import { WishlistItemsQueryKey } from '@/common/hooks/useItemWishlist/useItemWishlist';
import { AuthContext } from '@/contexts/auth/AuthContext';
import { BfwUserContextService, PagedWishlistItems } from 'generatedBfw/static';

export const useRemoveFromWishlist = (
  options?: Omit<UseMutationOptions<Response, Error, number>, 'mutationFn'> | undefined,
): UseMutationResult<Response, Error, number> => {
  const { fetchedUserInfo } = useContext(AuthContext);

  const queryClient = useQueryClient();

  return useMutation(
    (itemCollectionId: number) => {
      if (!fetchedUserInfo) {
        return Promise.reject('Cannot use useRemoveFromWishlist before fetching user info');
      }

      return BfwUserContextService.wishlistRemoveSingleItem(itemCollectionId);
    },
    {
      ...options,
      onMutate: (itemCollectionId: number) => {
        // artwork is removed from cache instantly the mutation is started, so change is reflected fast on UI.
        removeArtworkFromCachedWishlist(itemCollectionId, queryClient);

        options?.onMutate && options?.onMutate(itemCollectionId);
      },
      onSuccess: (data, variables, context) => {
        // after successful removal from wishlist, update the pages offsets and limits, so loading next pages is not getting duplicates
        updateCachedWishlistPageSizes(queryClient);

        // update wishlist and cart numbers on header
        queryClient.invalidateQueries(HeaderContentQueryKey);

        options?.onSuccess && options?.onSuccess(data, variables, context);
      },
    },
  );
};

const removeArtworkFromCachedWishlist = (removedId: number, queryClient: QueryClient) => {
  const wishlistCache = queryClient.getQueriesData(WishlistItemsQueryKey);
  if (wishlistCache.length > 0) {
    const wishlistValue = wishlistCache[0][1] as InfiniteData<PagedWishlistItems>;
    const wishlistWithoutItem = wishlistValue.pages.map(
      (page) =>
        <PagedWishlistItems>{
          ...page,
          items: page.items.filter((item) => item.itemCollectionId !== removedId),
        },
    );

    wishlistValue.pages = wishlistWithoutItem;
    queryClient.setQueryData(WishlistItemsQueryKey, wishlistValue);
  }
};

const updateCachedWishlistPageSizes = (queryClient: QueryClient) => {
  const wishlistCache = queryClient.getQueriesData(WishlistItemsQueryKey);
  if (wishlistCache.length === 0) {
    return;
  }

  let itemCounter = 0;
  const wishlistValue = wishlistCache[0][1] as InfiniteData<PagedWishlistItems>;

  const wishlistWithoutItem = wishlistValue.pages.map((page) => {
    const offset = itemCounter;
    itemCounter += page.items.length;
    return <PagedWishlistItems>{
      ...page,
      limit: page.items.length,
      offset,
    };
  });

  wishlistValue.pages = wishlistWithoutItem;
  queryClient.setQueryData(WishlistItemsQueryKey, wishlistValue);
};
